import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { usePromptDataProfileCopyMutation } from "../../services/server/PromptDataProfileApi";
import type { PromptDataProfileCopyEntity } from "../../types/PromptDataProfileCopyEntity";
import promptDataProfileCopy from "./prompt-data-profile-copy.json";

export const PromptDataProfileCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = promptDataProfileCopy as FormJson;

    const [formData, setFormData] = useState<PromptDataProfileCopyEntity>({} as PromptDataProfileCopyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = usePromptDataProfileCopyMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: result.data.id,
                            resource: "PromptDataProfile",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.PROMPT_DATA_PROFILES}/${FORM_ACTION.EDIT}/${result.data.id}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    const { id } = useParams();
    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<PromptDataProfileCopyEntity>
                    name="PromptDataProfileCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
